import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { AdminService, Relative } from '../../services/admin.service';

@Component({
  selector: 'app-manage-relatives',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule],
  templateUrl: './manage-relatives.component.html',
  styleUrls: ['./manage-relatives.component.css']
})
export class ManageRelativesComponent implements OnInit {
  relatives: Relative[] = [];
  filteredRelatives: Relative[] = [];
  relativesData: any = null;
  searchTerm = '';
  isLoading = false;
  Math = Math;

  // Modal state
  showModal = false;
  isEditing = false;
  editingRelative: Relative | null = null;
  showPassword = false;
  isPasswordMasked = false;

  // Profile picture upload
  avatarPreviewUrl: string | null = null;
  avatarUploading = false;

  // Referral links
  parentReferralLink: string = '';
  spouseReferralLink: string = '';
  siblingReferralLink: string = '';
  baseUrl: string = '';

  // Form
  relativeForm!: FormGroup;

  constructor(
    public authService: AuthService,
    private adminService: AdminService,
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    console.log('🔍 ManageRelativesComponent - ngOnInit called');
    console.log('🔍 ManageRelativesComponent - AuthService isAuthenticated:', this.authService.isAuthenticated());
    console.log('🔍 ManageRelativesComponent - AuthService getToken:', this.authService.getToken() ? 'EXISTS' : 'NULL');
    // Get base URL for referral links
    this.baseUrl = window.location.origin;
    this.loadRelatives();
    this.initializeForm();
    
    // Check for query parameters to auto-open add form
    this.route.queryParams.subscribe(params => {
      if (params['action'] === 'add') {
        this.openAddRelativeModal();
      }
    });
  }

  initializeForm(): void {
    this.relativeForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      middleName: [''],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [this.optionalEmailValidator]],
      mobileNumber: ['', [Validators.required, Validators.pattern(/^[0-9+\-\s()]+$/)]],
      dateOfBirth: ['', Validators.required],
      gender: ['', Validators.required],
      maritalStatus: [''],
      qualification: [''],
      profession: [''],
      location: [''],
      referralCode: ['', [Validators.pattern(/^[A-Z0-9]{6,12}$/)]], // Admin can update referral code
      parentReferralCode: [''],
      spouseReferralCode: [''],
      isDeceased: [false],
      username: ['', [Validators.required, Validators.minLength(3)]],
      password: ['', [Validators.minLength(6)]] // Removed required validator
    });
    // Allow both parentReferralCode and spouseReferralCode to be provided simultaneously
    // This enables a person to be both a child (to someone) and a spouse (to someone else)
    this.relativeForm.updateValueAndValidity({ emitEvent: false });
  }

  private requireOneOfValidator(fieldA: string, fieldB: string) {
    return (group: AbstractControl): ValidationErrors | null => {
      const a = (group.get(fieldA)?.value || '').toString().trim();
      const b = (group.get(fieldB)?.value || '').toString().trim();
      return !a && !b ? { oneOfRequired: true } : null;
    };
  }

  optionalEmailValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value || value.trim() === '') {
      return null; // Allow empty values
    }
    // If value is provided, validate email format
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(value) ? null : { email: true };
  }

  loadRelatives(page: number = 1): void {
    this.isLoading = true;
    this.adminService.getRelatives(page, 20, this.searchTerm || undefined).subscribe({
      next: (data:any) => {
        if(data && data.data && data.data.relatives){
          this.relatives = data.data.relatives;
          this.filteredRelatives = [...this.relatives];
          this.relativesData = data.data;
          this.isLoading = false;
        }
      },
      error: (error) => {
        console.error('❌ Error loading relatives:', error);
        this.isLoading = false;
      }
    });
  }

  filterRelatives(): void {
    // Use server-side search instead of client-side filtering
    this.loadRelatives(1);
  }

  openAddRelativeModal(): void {
    console.log('🔍 Opening add relative modal');
    this.isEditing = false;
    this.editingRelative = null;
    this.isLoading = false; // Reset loading state
    this.showPassword = false;
    this.isPasswordMasked = false;
    this.avatarPreviewUrl = null;
    this.relativeForm.reset();
    this.showModal = true;
    // Auto-generate credentials for new relatives
    this.generateDefaultCredentials();
    console.log('🔍 Form after reset and generation:', this.relativeForm.value);
    console.log('🔍 Form valid after reset:', this.relativeForm.valid);
  }

  editRelative(relative: Relative): void {
    this.isEditing = true;
    this.editingRelative = relative;
    this.showPassword = false;
    this.isPasswordMasked = true;
    
    // Set avatar preview URL if relative has profile image
    this.avatarPreviewUrl = relative.profileImageUrl || null;
    
    // Generate referral links for this relative
    this.generateReferralLinks(relative);
    
    // Always start with masked password for security
    this.relativeForm.patchValue({
      firstName: relative.firstName,
      middleName: relative.middleName || '',
      lastName: relative.lastName,
      email: relative.email,
      mobileNumber: relative.mobileNumber,
      dateOfBirth: relative.dateOfBirth.split('T')[0], // Convert to YYYY-MM-DD format
      gender: relative.gender,
      maritalStatus: (relative as any).maritalStatus || '',
      qualification: relative.qualification || '',
      profession: relative.profession || '',
      location: (relative as any).location || '',
      referralCode: relative.referralCode || '', // Show current referral code for editing
      parentReferralCode: relative.parentReferralCode || '',
      spouseReferralCode: relative.spouseReferralCode || '', // Populate spouse referral code
      isDeceased: relative.isDeceased,
      username: relative.username || '',
      password: '••••••••' // Always start with masked password
    });
    
    this.showModal = true;
  }

  deleteRelative(relativeId: number): void {
    if (confirm('Are you sure you want to delete this relative? This action cannot be undone.')) {
      this.isLoading = true;
      this.adminService.deleteRelative(relativeId).subscribe({
        next: (response) => {
          console.log('Relative deleted:', response);
          this.loadRelatives();
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error deleting relative:', error);
          this.isLoading = false;
        }
      });
    }
  }

  toggleRelativeStatus(relativeId: number): void {
    this.isLoading = true;
    this.adminService.toggleRelativeStatus(relativeId).subscribe({
      next: (response) => {
        console.log('Relative status toggled:', response);
        this.loadRelatives();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error toggling relative status:', error);
        this.isLoading = false;
      }
    });
  }


  saveRelative(): void {
    // Clear any previous form-level errors
    this.relativeForm.setErrors(null);
    console.log('🔍 saveRelative called');
    console.log('🔍 Form valid:', this.relativeForm.valid);
    console.log('🔍 Form invalid:', this.relativeForm.invalid);
    console.log('🔍 Form value:', this.relativeForm.value);
    console.log('🔍 Form errors:', this.relativeForm.errors);
    console.log('🔍 isLoading:', this.isLoading);
    
    // Check form validity first
    if (!this.relativeForm.valid) {
      console.log('🔍 Form is invalid, marking all fields as touched');
      this.relativeForm.markAllAsTouched();
      return;
    }
    
    // Check if already loading
    if (this.isLoading) {
      console.log('🔍 Already loading, ignoring request');
      return;
    }
    
    this.isLoading = true;
    const formData = this.relativeForm.value;
    
    // Handle multiple relationship types - a person can be both a child and a spouse
    let relationshipType = null;
    const hasParentReferral = formData.parentReferralCode && formData.parentReferralCode.trim() !== '';
    const hasSpouseReferral = formData.spouseReferralCode && formData.spouseReferralCode.trim() !== '';
    
    if (hasParentReferral && hasSpouseReferral) {
      // Person is both a child (to someone) and a spouse (to someone else)
      relationshipType = 'Child-Spouse'; // Combined relationship type
    } else if (hasParentReferral) {
      relationshipType = 'Child'; // Only child relationship
    } else if (hasSpouseReferral) {
      relationshipType = 'Spouse'; // Only spouse relationship
    }
    
    const relativeData: any = {
      ...formData,
      dateOfBirth: new Date(formData.dateOfBirth).toISOString(),
      isDeceased: Boolean(formData.isDeceased), // Explicitly convert to boolean
      relationshipType: relationshipType // Automatically set based on referral codes
    };

    // Handle optional fields - convert empty strings to null
    if (!formData.email || formData.email.trim() === '') {
      relativeData.email = null;
    }

    if (!formData.maritalStatus || formData.maritalStatus.trim() === '') {
      relativeData.maritalStatus = null;
    }

    // Enforce exactly one referral path based on referredBy
    // Independent optional referrals
    if (!formData.parentReferralCode || formData.parentReferralCode.trim() === '') {
      relativeData.parentReferralCode = null;
    }
    // Relationship type no longer required; backend will infer or ignore
    if (!formData.spouseReferralCode || formData.spouseReferralCode.trim() === '') {
      relativeData.spouseReferralCode = null;
    }

    // Remove password from update data if it's empty or masked (for editing)
    if (this.isEditing && (!formData.password || formData.password.trim() === '' || formData.password === '••••••••')) {
      delete relativeData.password;
    }

    if (this.isEditing && this.editingRelative) {
      console.log('🔍 Updating existing relative');
      this.adminService.updateRelative(this.editingRelative.id, relativeData).subscribe({
        next: (response) => {
          console.log('✅ Relative updated:', response);
          this.closeModal();
          this.loadRelatives();
          this.isLoading = false;
        },
        error: (error) => {
          console.error('❌ Error updating relative:', error);
          this.isLoading = false;
        }
      });
    } else {
      console.log('🔍 Adding new relative');
      // For new relatives, password is required
      if (!formData.password || formData.password.trim() === '') {
        console.log('🔍 Password is required for new relative');
        this.relativeForm.get('password')?.setErrors({ required: true });
        this.isLoading = false;
        return;
      }
      
      console.log('🔍 Sending relative data:', relativeData);
      console.log('🔍 Auth token:', this.authService.getToken());
      
      this.adminService.addRelative(relativeData).subscribe({
        next: (response) => {
          console.log('✅ Relative added:', response);
          this.closeModal();
          this.loadRelatives();
          this.isLoading = false;
        },
        error: (error) => {
          console.error('❌ Error adding relative:', error);
          console.error('❌ Error status:', error.status);
          console.error('❌ Error message:', error.message);
          console.error('❌ Error details:', error.error);
          this.isLoading = false;
        }
      });
    }
  }

  closeModal(): void {
    console.log('🔍 Closing modal');
    this.showModal = false;
    this.isEditing = false;
    this.editingRelative = null;
    this.isLoading = false; // Reset loading state
    this.showPassword = false;
    this.relativeForm.reset();
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  }

  generateDefaultCredentials(): void {
    const firstName = this.relativeForm.get('firstName')?.value || '';
    const lastName = this.relativeForm.get('lastName')?.value || '';
    const email = this.relativeForm.get('email')?.value || '';
    
    // Generate username from first name and last name
    const username = firstName && lastName 
      ? `${firstName.toLowerCase()}${lastName.toLowerCase()}${Math.floor(Math.random() * 1000)}`
      : email.split('@')[0] + Math.floor(Math.random() * 1000);
    
    // Generate a secure password
    const password = this.generateSecurePassword();
    
    console.log('🔍 Generating credentials:', { username, password });
    
    this.relativeForm.patchValue({
      username: username,
      password: password
    });
    
    console.log('🔍 Form after credential generation:', this.relativeForm.value);
    console.log('🔍 Form valid after credential generation:', this.relativeForm.valid);
  }

  generateSecurePassword(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    
    // Ensure at least one of each type
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)]; // Uppercase
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]; // Lowercase
    password += '0123456789'[Math.floor(Math.random() * 10)]; // Number
    password += '!@#$%^&*'[Math.floor(Math.random() * 8)]; // Special char
    
    // Fill the rest randomly
    for (let i = 4; i < 12; i++) {
      password += chars[Math.floor(Math.random() * chars.length)];
    }
    
    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  }

  regenerateCredentials(): void {
    this.generateDefaultCredentials();
  }

  onNameChange(): void {
    // Only regenerate credentials for new relatives (not when editing)
    if (!this.isEditing) {
      this.generateDefaultCredentials();
    }
  }

  viewHierarchy(relativeId: number): void {
    this.router.navigate(['/hierarchy', relativeId]);
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
    
    // For both add and edit modes, the input type toggle happens automatically via the [type] binding
    // Additional logic for edit mode based on user role
    if (this.isEditing) {
      if (this.authService.isAdmin()) {
        // For admin users, fetch the actual password when they click show
        if (this.showPassword && this.editingRelative) {
          this.adminService.getRelativePassword(this.editingRelative.id).subscribe({
            next: (response) => {
              console.log('🔑 Admin - Retrieved actual password for viewing:', this.editingRelative?.id);
              // Show the actual password in the input field
              this.relativeForm.patchValue({ password: response.password });
              this.isPasswordMasked = false;
            },
            error: (error) => {
              console.error('🔑 Admin - Failed to retrieve password for viewing:', error);
              // Keep the masked password if API fails
              this.relativeForm.patchValue({ password: '••••••••' });
            }
          });
        } else if (!this.showPassword) {
          // Hide the password by masking it again
          this.relativeForm.patchValue({ password: '••••••••' });
          this.isPasswordMasked = true;
        }
      } else {
        // For non-admin users, show a message that actual password cannot be retrieved
        console.log('🔒 Security: Actual password cannot be displayed for security reasons');
      }
    }
  }

  getCurrentPassword(): string {
    if (this.isEditing && this.editingRelative) {
      // In a real application, you would need to call an API to get the current password
      // For now, we'll return a placeholder
      return '••••••••';
    }
    return '';
  }

  debugForm(): void {
    console.log('Form valid:', this.relativeForm.valid);
    console.log('Form invalid:', this.relativeForm.invalid);
    console.log('Form value:', this.relativeForm.value);
    console.log('Form errors:', this.relativeForm.errors);
    console.log('Form controls:', this.relativeForm.controls);
    
    // Check each control
    Object.keys(this.relativeForm.controls).forEach(key => {
      const control = this.relativeForm.get(key);
      console.log(`${key}:`, {
        value: control?.value,
        valid: control?.valid,
        invalid: control?.invalid,
        errors: control?.errors,
        touched: control?.touched,
        dirty: control?.dirty
      });
    });
  }

  onAvatarSelected(event: any): void {
    const file: File | undefined = event?.target?.files?.[0];
    if (!file) { return; }
    this.avatarUploading = true;
    
    if (this.editingRelative) {
      this.adminService.uploadRelativeProfileImage(this.editingRelative.id, file).subscribe({
        next: (resp: any) => {
          const url = resp?.data || null;
          this.avatarPreviewUrl = url;
          this.avatarUploading = false;
        },
        error: (err) => {
          console.error(err);
          this.avatarUploading = false;
        }
      });
    }
  }

  generateReferralLinks(data: Relative): void {
    // Parent referral link - for adding children (uses own referral code)
    if (data.referralCode) {
      this.parentReferralLink = `${this.baseUrl}/signup?parentReferralCode=${data.referralCode}&parentName=${encodeURIComponent(data.firstName + ' ' + (data.lastName || ''))}`;
    } else {
      this.parentReferralLink = '';
    }

    // Spouse referral link - for adding spouse (uses own referral code)
    if (data.referralCode) {
      this.spouseReferralLink = `${this.baseUrl}/signup?spouseReferralCode=${data.referralCode}&spouseName=${encodeURIComponent(data.firstName + ' ' + (data.lastName || ''))}`;
    } else {
      this.spouseReferralLink = '';
    }

    // Sibling referral link - for adding siblings (uses parent's referral code)
    // Note: For siblings, we use the parent's referral code but don't include parent name
    // since siblings share the same parent
    if (data.parentReferralCode) {
      this.siblingReferralLink = `${this.baseUrl}/signup?parentReferralCode=${data.parentReferralCode}`;
    } else {
      this.siblingReferralLink = ''; // No parent, so no sibling link available
    }
  }

  copyToClipboard(text: string, type: string): void {
    if (!text) {
      return;
    }
    navigator.clipboard.writeText(text).then(() => {
      // You can add a toast notification here if needed
      console.log(`${type} referral link copied to clipboard`);
      alert(`${type} referral link copied to clipboard!`);
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert('Failed to copy link. Please try again.');
    });
  }

  shareLink(type: string): void {
    let link = '';
    switch(type) {
      case 'parent':
        link = this.parentReferralLink;
        break;
      case 'spouse':
        link = this.spouseReferralLink;
        break;
      case 'sibling':
        link = this.siblingReferralLink;
        break;
    }

    if (!link) {
      return;
    }

    if (navigator.share) {
      navigator.share({
        title: 'Join my family tree',
        text: `Join my family tree on Familitive using this link:`,
        url: link
      }).catch(err => {
        console.error('Error sharing:', err);
        // Fallback to clipboard
        this.copyToClipboard(link, type);
      });
    } else {
      // Fallback to clipboard if share API is not available
      this.copyToClipboard(link, type);
    }
  }
}
